import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Loader } from '@googlemaps/js-api-loader';
import { toastr } from 'react-redux-toastr';
import { GOOGLE_CLOUD_TOKEN } from '../../../../config/endpoints';

const loader = new Loader({
  apiKey: GOOGLE_CLOUD_TOKEN,
  libraries: ['places'],
  language: 'es',
});

let placesService = null;

export default function GooglePlacesInput({ input, meta, ...rest }) {
  const inputRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    loader.load().then(() => {
      if (!window.google) return;
      placesService = new window.google.maps.places.AutocompleteService();
    });
  }, []);

  const handleInputChange = (e) => {
    const { value } = e.target;

    input.onChange({ address: value });

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    if (value.length >= 3 && placesService) {
      debounceTimeout.current = setTimeout(() => {
        placesService.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country: 'pe' },
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              setSuggestions(predictions || []);
            } else {
              setSuggestions([]);
            }
          },
        );
      }, 300);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const map = new window.google.maps.Map(document.createElement('div'));
    const service = new window.google.maps.places.PlacesService(map);

    service.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ['formatted_address', 'geometry', 'name', 'address_components'],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const address = place.formatted_address || place.name;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          const postalCodeComponent = place.address_components.find(
            (component) => component.types.includes('postal_code'),
          );
          const postalCode = postalCodeComponent
            ? postalCodeComponent.long_name
            : null;

          const result = {
            address,
            lat,
            lng,
            postalCode,
          };

          input.onChange(result);
          setSuggestions([]);
        } else {
          toastr.error('Error', 'Error al obtener detalles del lugar');
        }
      },
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        {...rest}
        ref={inputRef}
        onChange={handleInputChange}
        className="form-control"
        value={(input.value && input.value.address) || ''}
        type="text"
        placeholder="Buscar dirección de entrega"
        style={{ width: '100%', padding: '8px' }}
      />
      {suggestions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #ccc',
            zIndex: 10,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {suggestions.map((sug) => (
            <li
              key={sug.place_id}
              onClick={() => handleSuggestionClick(sug)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  handleSuggestionClick(sug);
              }}
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
              role="button"
              tabIndex={0}
              style={{ padding: '8px', cursor: 'pointer' }}
            >
              {sug.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

GooglePlacesInput.propTypes = {
  input: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  meta: PropTypes.object,
};

GooglePlacesInput.defaultProps = {
  meta: null,
};
